import { Checkbox, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';

export interface ProfileCheckboxSectionProps {
  label: string;
  options: string[];
  columns?: number;
  value: string[];
  onChange: (value: string[]) => void;
  withAsterisk?: boolean;
  error?: string;
  notListedText?: string;
  onNotListedTextChange?: (text: string) => void;
}

/** Split items into column-major stacks of `ceil(n / columns)` so lists read down, then across. */
export function splitIntoColumnMajorColumns<T>(items: T[], columns: number): T[][] {
  const colCount = Math.max(1, columns);
  if (items.length === 0) {
    return Array.from({ length: colCount }, () => []);
  }
  const perCol = Math.ceil(items.length / colCount);
  return Array.from({ length: colCount }, (_, i) => items.slice(i * perCol, (i + 1) * perCol));
}

/**
 * Reusable checkbox group for profile/onboarding forms (e.g. languages, specialties, demographics).
 * Used in UserSettings and provider onboarding sections.
 */
export function ProfileCheckboxSection({
  label,
  options,
  columns = 3,
  value,
  onChange,
  withAsterisk,
  error,
  notListedText,
  onNotListedTextChange,
}: ProfileCheckboxSectionProps): JSX.Element {
  const showNotListedInput = value.includes('Not Listed') && onNotListedTextChange;

  return (
    <div>
      <Title order={5} mb="xs" c="dark">
        {label}
        {withAsterisk ? (
          <Text component="span" c="red" ml={4}>
            *
          </Text>
        ) : null}
      </Title>
      <Checkbox.Group value={value} onChange={onChange}>
        <SimpleGrid cols={columns} spacing="xs" verticalSpacing="xs">
          {splitIntoColumnMajorColumns(options, columns).map((col, colIndex) => (
            <Stack key={colIndex} gap="xs">
              {col.map((opt) => (
                <Checkbox key={opt} value={opt} label={opt} />
              ))}
            </Stack>
          ))}
        </SimpleGrid>
      </Checkbox.Group>
      {showNotListedInput && (
        <TextInput
          mt="sm"
          maw={360}
          placeholder="Please specify"
          value={notListedText ?? ''}
          onChange={(e) => onNotListedTextChange(e.currentTarget.value)}
        />
      )}
      {error ? (
        <Text size="xs" c="red" mt="xs">
          {error}
        </Text>
      ) : null}
    </div>
  );
}
